import React, { useCallback } from 'react';

import { FormattedMessage } from 'react-intl';

import { Inline, Text } from '@atlaskit/primitives/compiled';
import { ColorPicker, DEFAULT_GRAY_COLOR, useBuiltInColorMap } from '@atlassian/color-picker';
import { useProductDependencies } from '@atlassian/company-hub-common';
import { resolveAdfColorForPicker } from '@atlassian/company-hub-common/hub-brand-palette';
import { useHubTheming } from '@atlassian/company-hub-common/hub-theming-context';

import { CARDS_EXTENSION_CONFIG_BORDER_ANALYTIC_SOURCE } from '../../../../constants';
import type { CardsActions } from '../../../../controllers/use-cards-extension-parameters-and-actions/types';
import { messages } from './messages';

type BorderColorFieldProps = {
	borderColor: string | undefined;
	isCompanyHub: boolean;
	updateMainFormField: CardsActions['updateMainFormField'];
};

export const BorderColorField = ({
	borderColor,
	isCompanyHub,
	updateMainFormField,
}: BorderColorFieldProps): React.JSX.Element => {
	const { isSSRProductInDarkMode } = useProductDependencies();
	const { branding } = useProductDependencies();
	const { hideDefaultColorPalette, brandBackgroundColorMap: legacyBrandMap } = branding ?? {};
	const { backgroundColorMap } = useBuiltInColorMap(isSSRProductInDarkMode);
	const theming = useHubTheming();
	const isV2 = theming?.isAdvancedThemingEnabled ?? false;
	const palette = isV2 ? theming?.palette : undefined;
	const primaryColor = isV2 ? theming?.primaryColor : undefined;
	const v2BorderMap = isV2 ? theming?.brandBorderColorMap : undefined;

	const defaultBorderColor = DEFAULT_GRAY_COLOR;
	const resolvedBorderColor =
		isV2 && palette && primaryColor
			? resolveAdfColorForPicker(
					borderColor || defaultBorderColor,
					palette,
					'primary.600',
					primaryColor,
				)
			: borderColor || defaultBorderColor;

	const toStoredColor = theming?.toStoredColor;
	const handleChange = useCallback(
		(color: string) => {
			const next = isV2 && toStoredColor ? toStoredColor(color) : color;
			updateMainFormField({ borderColor: next });
		},
		[updateMainFormField, isV2, toStoredColor],
	);

	return (
		<Inline alignBlock="center" spread="space-between">
			<Text color="color.text">
				<FormattedMessage {...messages.borderColor} />
			</Text>
			<ColorPicker
				builtInColorMap={
					isV2 && v2BorderMap
						? v2BorderMap
						: hideDefaultColorPalette && legacyBrandMap
							? legacyBrandMap
							: backgroundColorMap
				}
				onChange={handleChange}
				value={resolvedBorderColor}
				pickerTitle={messages.borderColor}
				showCustomPalette={isCompanyHub}
				analyticsSource={CARDS_EXTENSION_CONFIG_BORDER_ANALYTIC_SOURCE}
			/>
		</Inline>
	);
};
